import { useLoader } from '@react-three/fiber';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FileLoaderOptionsContext } from '../FileLoaderOptionsContext';
import { Octree, OctreeHierarchyLoader, OctreeNode } from '../Octree';

const BANDWIDTH_TEST_SIZE = 50_000_000;
const BANDWIDTH_NUM_PARALLEL_FETCHES = 8;
const LATENCY_NUM_PARALLEL_FETCHES = 4;

const LATENCY_PROGRESS_WEIGHT = 0.1;

const fetchNoCache = async (
  url: string,
  withCredentials: boolean,
  requestHeader?: { [header: string]: string }
): Promise<number> => {
  try {
    const response = await fetch(url, {
      headers: {
        ...requestHeader,
      },
      cache: 'no-store',
      credentials: withCredentials ? 'include' : 'same-origin',
    });

    const result = await response.blob();
    return result.size;
  } catch {
    return -1;
  }
};

export type BandwidthBenchmarkResult = {
  totalbytes: number;
  timeElapsed: number;
  numberErrors: number;
  speedMbitPerSec: number;
};

const runDownloadBenchmark = (
  nodes: OctreeNode[],
  withCredentials: boolean,
  onProgress: (percent: number) => void
): Promise<BandwidthBenchmarkResult> => {
  const startTime = performance.now();

  let started = 0;
  let completed = 0;
  let totalbytes = 0;
  let numberErrors = 0;

  let resolve: (r: BandwidthBenchmarkResult) => void;
  const result = new Promise<BandwidthBenchmarkResult>((reolve_) => {
    resolve = reolve_;
  });

  const fetchOne = () => {
    if (started >= nodes.length) return;
    const node = nodes[started];
    started += 1;
    fetchNoCache(node.url, withCredentials, node.requestHeader).then((sizeBytes) => {
      completed += 1;
      if (sizeBytes < 0) {
        numberErrors += 1;
      } else {
        totalbytes += sizeBytes;
      }
      onProgress(Math.round((completed / nodes.length) * 100));
      if (completed === nodes.length) {
        const timeElapsed = performance.now() - startTime;
        const totalMbits = totalbytes * 8 * 0.000001;
        const seconds = timeElapsed * 0.001;
        resolve({
          numberErrors,
          totalbytes,
          timeElapsed: Math.round(seconds),
          speedMbitPerSec: seconds > 0 ? Math.round(totalMbits / seconds) : 0,
        });
      } else {
        fetchOne();
      }
    });
  };

  for (let index = 0; index < BANDWIDTH_NUM_PARALLEL_FETCHES; index += 1) {
    fetchOne();
  }

  return result;
};

export type LatencyBenchmarkResult = {
  latencies: number[];
  avgLatency: number;
  minLatency: number;
  numberErrors: number;
};

const runLatencyBenchmark = (
  nodes: OctreeNode[],
  withCredentials: boolean,
  onProgress: (percent: number) => void
): Promise<LatencyBenchmarkResult> => {
  let resolve: (r: LatencyBenchmarkResult) => void;
  const result = new Promise<LatencyBenchmarkResult>((resolve_) => {
    resolve = resolve_;
  });

  const latencies: number[] = [];

  let started = 0;
  let completed = 0;
  let numberErrors = 0;

  const fetchOne = () => {
    if (started >= nodes.length) return;
    const node = nodes[started];
    started += 1;
    const { url, requestHeader } = node;
    const startTime = performance.now();
    fetchNoCache(url, withCredentials, { ...requestHeader, range: 'bytes=0-8' }).then(
      (sizeBytes) => {
        const timeElapsed = Math.max(1, Math.round(performance.now() - startTime));
        completed += 1;
        if (sizeBytes < 0) {
          numberErrors += 1;
        } else {
          latencies.push(timeElapsed);
        }
        onProgress(Math.round((completed / nodes.length) * 100));
        if (completed === nodes.length) {
          const minLatency = latencies.reduce((minLat, latency) => {
            return Math.min(minLat, latency);
          }, 999999);
          const sumLatency = latencies.reduce((sumLat, latency) => {
            return sumLat + latency;
          }, 0);
          resolve({
            latencies,
            avgLatency: latencies.length > 0 ? Math.round(sumLatency / latencies.length) : 0,
            minLatency,
            numberErrors,
          });
        } else {
          fetchOne();
        }
      }
    );
  };

  for (let index = 0; index < LATENCY_NUM_PARALLEL_FETCHES; index += 1) {
    fetchOne();
  }
  return result;
};

export type NetworkBenchmarkResult = {
  latencyResult: LatencyBenchmarkResult;
  downloadResult: BandwidthBenchmarkResult;
};

export interface NetworkBenchmarkProps {
  octreeUrl: string;
  requestHeader?: { [header: string]: string };
  onComplete: (result: NetworkBenchmarkResult) => void;
  onProgress?: (percent: number) => void;
}

export const NetworkBenchmark = ({
  octreeUrl,
  requestHeader,
  onComplete,
  onProgress,
}: NetworkBenchmarkProps) => {
  const fileLoaderOptions = useContext(FileLoaderOptionsContext);
  const { withCredentials } = fileLoaderOptions;
  const octreeData: Octree = useLoader(OctreeHierarchyLoader, octreeUrl, (loader) => {
    loader.setWithCredentials(withCredentials);
    if (requestHeader) {
      loader.setRequestHeader(requestHeader);
    }
  });

  useEffect(() => {
    return () => {
      useLoader.clear(OctreeHierarchyLoader, octreeUrl);
    };
  }, [octreeUrl]);

  const octreeNodes = useMemo(() => {
    const nodes: OctreeNode[] = [];
    let totalSize = 0;
    const addNodeWithChildren = (parent: OctreeNode) => {
      if (totalSize > BANDWIDTH_TEST_SIZE) {
        return;
      }
      totalSize += parent.dataFormat.recordSize * parent.numPoints;
      nodes.push(parent);
      parent.children.forEach((child: OctreeNode) => {
        addNodeWithChildren(child);
      });
    };
    addNodeWithChildren(octreeData.root);
    return nodes;
  }, [octreeData]);

  const [currentBenchmark, setCurrentBenchmark] = useState<string>('latency');

  const [latencyResult, setLatencyResult] = useState<LatencyBenchmarkResult | undefined>();

  useEffect(() => {
    if (currentBenchmark === 'latency') {
      runLatencyBenchmark(octreeNodes, withCredentials, (percent) => {
        if (onProgress) {
          onProgress(Math.round(percent * LATENCY_PROGRESS_WEIGHT));
        }
      }).then((result) => {
        setLatencyResult(result);
        setCurrentBenchmark('download');
      });
    }
  }, [octreeNodes, withCredentials, currentBenchmark, onProgress]);

  const [downloadResult, setDownloadResult] = useState<BandwidthBenchmarkResult | undefined>();

  useEffect(() => {
    if (currentBenchmark === 'download') {
      runDownloadBenchmark(octreeNodes, withCredentials, (percent) => {
        if (onProgress) {
          onProgress(
            Math.round(100 * LATENCY_PROGRESS_WEIGHT + percent * (1 - LATENCY_PROGRESS_WEIGHT))
          );
        }
      }).then(setDownloadResult);
    }
  }, [octreeNodes, withCredentials, currentBenchmark, onProgress]);

  useEffect(() => {
    if (latencyResult && downloadResult) {
      onComplete({ latencyResult, downloadResult });
    }
  }, [latencyResult, downloadResult, onComplete]);

  return <></>;
};
NetworkBenchmark.displayName = 'NetworkBenchmark';
